import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FaStar,
  FaCheck,
  FaGift,
  FaShieldAlt,
  FaClock,
  FaCreditCard,
  FaGamepad,
  FaHeadset,
  FaArrowRight,
  FaTimes,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import { generateJSONLD } from '../utils/seo';
import RatingChart from '../components/RatingChart';
import BonusComparisonChart from '../components/BonusComparisonChart';

interface OfferData {
  id: string;
  name: string;
  bonus: string;
  rating: number;
  minDeposit: string;
  wagering: string;
  payoutTime: string;
  license: string;
  image: string;
  description: string;
  pros: string[];
  cons: string[];
  ratings: { category: string; score: number }[];
  gameProviders: string[];
  paymentMethods: string[];
  url: string;
}

const OfferReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // Mock data - in production, this would come from an API
  const offers: Record<string, OfferData> = {
    casinocasino: {
      id: 'casinocasino',
      name: 'CasinoCasino',
      bonus: '100% up to £500 + 50 Spins',
      rating: 4.8,
      minDeposit: '£10',
      wagering: '35x',
      payoutTime: '24-48h',
      license: 'UKGC',
      image: '/images/offers/casinocasino.webp',
      description: `CasinoCasino stands out as one of the premier destinations for UK players seeking an exceptional online gaming experience. 
      With a generous welcome package that doubles your first deposit up to £500 and adds 50 free spins to sweeten the deal, this casino makes 
      a compelling first impression. The platform combines sleek design with intuitive navigation, ensuring you can find your favorite games 
      without any hassle.`,
      pros: [
        'Generous welcome bonus package',
        'Extensive game library with top providers',
        'Fast withdrawal processing',
        'Excellent mobile compatibility',
        '24/7 customer support',
        'VIP rewards program',
      ],
      cons: [
        'Wagering requirements could be lower',
        'Limited cryptocurrency options',
      ],
      ratings: [
        { category: 'Games', score: 9.2 },
        { category: 'Bonuses', score: 8.8 },
        { category: 'Support', score: 9.0 },
        { category: 'Payments', score: 8.5 },
        { category: 'Mobile', score: 9.5 },
        { category: 'Security', score: 9.3 },
      ],
      gameProviders: [
        'NetEnt',
        'Microgaming',
        'Evolution Gaming',
        'Play\'n GO',
        'Pragmatic Play',
        'Betsoft',
      ],
      paymentMethods: [
        'Visa',
        'Mastercard',
        'PayPal',
        'Skrill',
        'Neteller',
        'Bank Transfer',
      ],
      url: 'https://www.casinocasino.com',
    },
    mrplay: {
      id: 'mrplay',
      name: 'MrPlay',
      bonus: '200% up to £200 + 100 Spins',
      rating: 4.7,
      minDeposit: '£20',
      wagering: '30x',
      payoutTime: '12-24h',
      license: 'UKGC',
      image: '/images/offers/mrplay.webp',
      description: `MrPlay delivers an impressive gaming experience with one of the most attractive welcome bonuses in the UK market. 
      The 200% match bonus up to £200, combined with 100 free spins, provides substantial value for new players. The casino excels 
      in creating an engaging atmosphere with regular promotions and a loyalty program that rewards consistent play.`,
      pros: [
        'Excellent welcome bonus value',
        'Lower wagering requirements',
        'Quick payout times',
        'Regular promotions',
        'User-friendly interface',
      ],
      cons: [
        'Higher minimum deposit',
        'Limited live dealer games',
      ],
      ratings: [
        { category: 'Games', score: 8.8 },
        { category: 'Bonuses', score: 9.2 },
        { category: 'Support', score: 8.7 },
        { category: 'Payments', score: 8.8 },
        { category: 'Mobile', score: 9.0 },
        { category: 'Security', score: 9.0 },
      ],
      gameProviders: [
        'NetEnt',
        'Microgaming',
        'Evolution Gaming',
        'Play\'n GO',
      ],
      paymentMethods: [
        'Visa',
        'Mastercard',
        'PayPal',
        'Skrill',
      ],
      url: 'https://www.mrplay.com/',
    },
    partycasino: {
      id: 'partycasino',
      name: 'PartyCasino',
      bonus: '150% up to £300 + 75 Spins',
      rating: 4.9,
      minDeposit: '£10',
      wagering: '35x',
      payoutTime: '24-48h',
      license: 'UKGC',
      image: '/images/offers/partycasino.webp',
      description: `PartyCasino brings the party to online gaming with a vibrant platform and exceptional player experience. 
      The welcome offer provides a solid foundation with 150% match up to £300 and 75 free spins. What sets this casino apart 
      is its commitment to player satisfaction, evident in its responsive customer service and comprehensive game selection.`,
      pros: [
        'Outstanding customer service',
        'Wide variety of games',
        'Regular cashback offers',
        'Strong security measures',
        'Excellent mobile app',
        'Fast verification process',
      ],
      cons: [
        'Some games restricted in certain regions',
      ],
      ratings: [
        { category: 'Games', score: 9.5 },
        { category: 'Bonuses', score: 9.0 },
        { category: 'Support', score: 9.5 },
        { category: 'Payments', score: 9.2 },
        { category: 'Mobile', score: 9.4 },
        { category: 'Security', score: 9.3 },
      ],
      gameProviders: [
        'NetEnt',
        'Microgaming',
        'Evolution Gaming',
        'Play\'n GO',
        'Pragmatic Play',
        'IGT',
      ],
      paymentMethods: [
        'Visa',
        'Mastercard',
        'PayPal',
        'Skrill',
        'Neteller',
        'Bank Transfer',
        'Apple Pay',
      ],
      url: 'https://www.partycasino.com/',
    },
  };

  const offer = offers[id || ''] || offers.casinocasino;

  const comparisonData = [
    { casino: offer.name, bonus: 500, color: 'bg-gradient-to-r from-accent-primary to-accent-secondary' },
    { casino: 'Average', bonus: 350, color: 'bg-gradient-to-r from-gray-600 to-gray-500' },
  ];

  const schema = generateJSONLD({
    type: 'Article',
    headline: `${offer.name} Review - ${offer.bonus}`,
    description: offer.description,
    image: `https://gameobsidian.com${offer.image}`,
    datePublished: '2025-12-02',
    dateModified: '2025-12-02',
    author: {
      '@type': 'Organization',
      name: 'GameObsidian',
    },
    publisher: {
      '@type': 'Organization',
      name: 'GameObsidian',
      logo: {
        '@type': 'ImageObject',
        url: 'https://gameobsidian.com/images/logo.webp',
      },
    },
  });

  return (
    <>
      <SEO
        title={`${offer.name} Review - ${offer.bonus}`}
        description={`Complete review of ${offer.name}. ${offer.description.substring(0, 150)}...`}
        url={`/offers/${offer.id}`}
        type="article"
        image={`https://gameobsidian.com${offer.image}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schema }}
      />

      <div className="min-h-screen pt-20 pb-20">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm text-dark-muted">
            <Link to="/" className="hover:text-accent-primary transition-colors">
              Home
            </Link>
            <span className="mx-2">/</span>
            <Link
              to="/comparison"
              className="hover:text-accent-primary transition-colors"
            >
              Offers
            </Link>
            <span className="mx-2">/</span>
            <span className="text-dark-text">{offer.name}</span>
          </nav>

          {/* Header */}
          <div className="glass-effect rounded-2xl p-8 mb-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0">
                <img
                  src={offer.image}
                  alt={offer.name}
                  className="w-full md:w-64 h-48 object-contain rounded-lg"
                  width="256"
                  height="192"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-4">
                  <h1 className="text-4xl font-bold">{offer.name}</h1>
                  <div className="flex items-center gap-1 bg-accent-gold/20 px-3 py-1 rounded-full">
                    <FaStar className="text-accent-gold" />
                    <span className="font-bold">{offer.rating}</span>
                  </div>
                </div>
                <p className="text-2xl text-accent-primary font-semibold mb-4">
                  {offer.bonus}
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <FaShieldAlt className="text-accent-primary" />
                    <span>{offer.license} Licensed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaGift className="text-accent-primary" />
                    <span>Min Deposit: {offer.minDeposit}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaClock className="text-accent-primary" />
                    <span>Payout: {offer.payoutTime}</span>
                  </div>
                </div>
                <a
                  href={offer.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-6 px-8 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 rounded-lg font-semibold transition-opacity text-center"
                >
                  Claim Bonus Now
                </a>
              </div>
            </div>
          </div>

          {/* Rating Chart */}
          <div className="glass-effect rounded-2xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6">
              Detailed <span className="text-gradient">Ratings</span>
            </h2>
            <RatingChart ratings={offer.ratings} />
          </div>

          {/* Description */}
          <div className="glass-effect rounded-2xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-4">Overview</h2>
            <p className="text-lg text-dark-muted leading-relaxed whitespace-pre-line">
              {offer.description}
            </p>
          </div>

          {/* Pros and Cons */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="glass-effect rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <FaCheck className="text-green-500" />
                Pros
              </h3>
              <ul className="space-y-2">
                {offer.pros.map((pro, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <FaCheck className="text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-dark-muted">{pro}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass-effect rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <FaTimes className="text-red-500" />
                Cons
              </h3>
              <ul className="space-y-2">
                {offer.cons.map((con, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <FaTimes className="text-red-500 flex-shrink-0 mt-1" />
                    <span className="text-dark-muted">{con}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bonus Comparison */}
          <div className="glass-effect rounded-2xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6">
              Bonus <span className="text-gradient">Comparison</span>
            </h2>
            <BonusComparisonChart data={comparisonData} />
          </div>

          {/* Game Providers */}
          <div className="glass-effect rounded-2xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-4 flex items-center gap-2">
              <FaGamepad className="text-accent-primary" />
              Game Providers
            </h2>
            <div className="flex flex-wrap gap-3">
              {offer.gameProviders.map((provider, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-dark-card rounded-lg text-sm"
                >
                  {provider}
                </span>
              ))}
            </div>
          </div>

          {/* Payment Methods */}
          <div className="glass-effect rounded-2xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-4 flex items-center gap-2">
              <FaCreditCard className="text-accent-primary" />
              Payment Methods
            </h2>
            <div className="flex flex-wrap gap-3">
              {offer.paymentMethods.map((method, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-dark-card rounded-lg text-sm"
                >
                  {method}
                </span>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="glass-effect rounded-2xl p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Play?</h2>
            <p className="text-dark-muted mb-6">
              Claim your exclusive bonus and start playing at {offer.name} today
            </p>
            <a
              href={offer.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 rounded-lg font-semibold text-lg transition-opacity"
            >
              Claim Bonus Now
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default OfferReview;

